import { AfterViewInit, Component, ElementRef, EventEmitter, NgZone, OnDestroy, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GoogleMapsService } from '../../google-maps.service';

declare const google: any;

@Component({
  selector: 'app-address-auto-complete',
  standalone: true,
  imports: [
    CommonModule
  ],
  template: `
    <div class="address-autocomplete">
      <label>Enter your address</label>
      <div #host class="autocomplete-host"></div>
    </div>
  `,
  styleUrls: ['./address-auto-complete.component.scss']
})
export class AddressAutoCompleteComponent implements AfterViewInit, OnDestroy {
  @ViewChild('host', { static: true }) host!: ElementRef<HTMLDivElement>;
  @Output() addressSelected = new EventEmitter<{
    street: string;
    city: string;
    state: string;
    stateCode: string;
    zip: string;
  }>();

  private el: any | null = null;
  private onSelect?: (e: any) => void;

  // State code to full state name mapping
  private stateCodeToName: { [key: string]: string } = {
    'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
    'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
    'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
    'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
    'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
    'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
    'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
    'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
    'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
    'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
  };

  constructor(
    private ngZone: NgZone,
    private googleMapsService: GoogleMapsService
  ) {}

  async ngAfterViewInit() {
    try {
      await this.googleMapsService.load();
      await this.googleMapsService.placesReady;
      this.mount();
    } catch (error) {
    }
  }

  ngOnDestroy() {
    this.unmount();
  }

  private mount() {
    // Remove previous element and listener
    if (this.el && this.onSelect) {
      this.el.removeEventListener('gmp-placeselect', this.onSelect);
    }
    this.host.nativeElement.innerHTML = '';

    try {
      // Create the element using Google's API
      this.el = new google.maps.places.PlaceAutocompleteElement({
        types: ['address'],
        componentRestrictions: { country: 'us' }
      });

      // Basic component styling
      this.el.style.width = '100%';
      this.el.style.display = 'block';
      
      // Set placeholder via attribute
      this.el.setAttribute('placeholder', 'Start typing an address...');

      this.onSelect = async (e: any) => {
        try {
          // Handle new Google API format (gmp-select event)
          let place = e?.place;
          
          // If it's the new API format with placePrediction
          if (e?.placePrediction && !place) {
            place = e.placePrediction.toPlace();
          }
          
          if (!place) {
            return;
          }

          // Always fetch fresh data
          await place.fetchFields({
            fields: ['addressComponents', 'formattedAddress']
          });

          const comps = place.addressComponents ?? [];
          const get = (t: string) => comps.find((c: any) => c.types?.includes(t))?.longText ?? '';
          const getShort = (t: string) => comps.find((c: any) => c.types?.includes(t))?.shortText ?? '';

          const street = [get('street_number'), get('route')].filter(Boolean).join(' ').trim();
          const city = get('locality') || get('administrative_area_level_2') || '';
          const stateCode = getShort('administrative_area_level_1');
          const stateName = this.stateCodeToName[stateCode] || get('administrative_area_level_1') || stateCode;
          const zip = get('postal_code');
          const zip4 = get('postal_code_suffix');
          const fullZip = zip4 ? `${zip}-${zip4}` : zip;

          // Emit inside Angular zone
          this.ngZone.run(() => {
            this.addressSelected.emit({
              street: street,
              city: city,
              state: stateName,
              stateCode: stateCode,
              zip: fullZip
            });
          });
          
          // Note: Can't access internal input due to closed shadow DOM
          // The web component should maintain its own state

        } catch (err) {
        }
      };

      // Add event listeners for both new and old Google API
      this.el.addEventListener('gmp-select', this.onSelect);
      this.el.addEventListener('gmp-placeselect', this.onSelect);
      
      this.host.nativeElement.appendChild(this.el);

    } catch (error) {
    }
  }

  private unmount() {
    if (this.el && this.onSelect) {
      this.el.removeEventListener('gmp-select', this.onSelect);
      this.el.removeEventListener('gmp-placeselect', this.onSelect);
    }
    if (this.el?.parentNode) {
      this.el.parentNode.removeChild(this.el);
    }
    this.el = null;
    this.onSelect = undefined;
  }
}
